import { useRef, useState } from "react";
import EmailEditor from "react-email-editor";
import api from "@/lib/api";
import { toast } from "sonner";
import EditorShell from "./EditorShell";
import { Save, Copy } from "lucide-react";
import TagsInput from "./TagsInput";

export default function DragDropEditor({ initial, onClose, onSaved }) {
  const editorRef = useRef(null);
  const [name, setName] = useState(initial?.name || "");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [tags, setTags] = useState(initial?.tags || []);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const onLoad = () => {
    if (initial?.design_json && editorRef.current?.editor) {
      try { editorRef.current.editor.loadDesign(initial.design_json); } catch (e) { /* ignore */ }
    }
    try {
      editorRef.current?.editor?.addEventListener?.("design:loaded", () => setReady(true));
    } catch (e) { /* older API */ }
    setTimeout(() => setReady(true), 4000);
  };

  const persist = ({ asNew }) => {
    if (!name.trim()) return toast.error("Template name required");
    if (!subject.trim()) return toast.error("Subject required");
    if (!editorRef.current?.editor) return toast.error("Builder not ready yet");
    setBusy(true);
    editorRef.current.editor.exportHtml(async ({ design, html }) => {
      try {
        const payload = {
          name: asNew ? `${name} (copy)` : name,
          subject, html, body: html, design_json: design, tags,
        };
        let saved;
        if (initial?.id && !asNew) {
          const { data } = await api.put(`/templates/${initial.id}`, payload);
          saved = data;
        } else {
          const { data } = await api.post("/templates", { ...payload, type: "builder" });
          saved = data;
        }
        toast.success(asNew ? "Saved as new template" : "Template saved");
        onSaved?.(saved);
        onClose?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Save failed");
      } finally {
        setBusy(false);
      }
    });
  };

  return (
    <EditorShell
      testid="drag-drop-editor"
      title={initial?.id ? "Edit Drag & Drop Template" : "New Drag & Drop Template"}
      subtitle="// editor / visual_builder"
      onClose={onClose}
      footer={
        <>
          {initial?.id && (
            <button
              type="button"
              onClick={() => persist({ asNew: true })}
              disabled={busy || !ready}
              data-testid="drag-drop-editor-save-as-new"
              className="cf-btn-secondary"
            >
              <Copy size={14} /> Save as new
            </button>
          )}
          <button
            type="button"
            onClick={() => persist({ asNew: false })}
            disabled={busy || !ready}
            data-testid="drag-drop-editor-save"
            className="cf-btn-primary"
          >
            <Save size={14} /> {busy ? "Saving..." : ready ? "Save Template" : "Loading builder..."}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Template name
          </label>
          <input
            data-testid="drag-drop-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="cf-input"
            placeholder="e.g. Q1 launch — visual"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Subject
          </label>
          <input
            data-testid="drag-drop-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="cf-input"
            placeholder="Big news from {{company}}"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Tags / folders
          </label>
          <TagsInput value={tags} onChange={setTags} testid="drag-drop-tags-input" />
        </div>
      </div>
      <div
        className="border border-zinc-800 bg-white"
        style={{ height: "700px" }}
        data-testid="drag-drop-canvas"
      >
        <EmailEditor
          ref={editorRef}
          onLoad={onLoad}
          minHeight="700px"
          options={{
            displayMode: "email",
            features: { preview: true, undoRedo: true, stockImages: true },
            mergeTags: {
              name: { name: "Contact Name", value: "{{name}}" },
              company: { name: "Company", value: "{{company}}" },
              email: { name: "Email", value: "{{email}}" },
            },
            tools: {
              text: { enabled: true },
              heading: { enabled: true },
              image: { enabled: true },
              button: { enabled: true },
              divider: { enabled: true },
              spacer: { enabled: true },
              html: { enabled: true },
              video: { enabled: true },
              social: { enabled: true },
              columns: { enabled: true },
              menu: { enabled: true },
            },
          }}
        />
      </div>
    </EditorShell>
  );
}
